import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { MatProgressBar } from '@angular/material/progress-bar'
import {
  ApiService,
  ClinicService,
  ConfirmService,
  CrudService,
  EstimateModel,
  FileService,
  Invoice,
  InvoiceData,
  InvoiceModel,
  Patient,
  ProgressIndicatorService,
  Transaction,
} from '@mmx/shared'
import { get } from 'lodash'
import { Subject } from 'rxjs'
import { filter, finalize, takeUntil } from 'rxjs/operators'

import {
  SendContactEditDialogResult,
  SendContactEditDialogService,
} from '../../send-contact-edit-dialog'

@Component({
  selector: 'mmx-invoice-card',
  templateUrl: './invoice-card.component.html',
  styleUrls: ['./invoice-card.component.scss'],
  standalone: false,
})
export class InvoiceCardComponent implements OnChanges, OnDestroy {
  @ViewChild(MatProgressBar) progressBar: MatProgressBar
  @Input() invoice: InvoiceModel
  @Input() patient: Patient
  @Input() estimate: EstimateModel
  @Input() onAppointmentPage: boolean
  @Input() style: string
  @Output() voidInvoice = new EventEmitter<InvoiceModel>()

  public STATUS = Invoice.STATUS
  public pendingAmount: number
  private onDestroy$ = new Subject<void>()

  constructor(
    private restService: ApiService,
    private clinicService: ClinicService,
    private sendContactEditDialogService: SendContactEditDialogService,
    private fileService: FileService,
    private confirmService: ConfirmService,
    private progressService: ProgressIndicatorService,
    private crudService: CrudService,
  ) {}

  get defaultCurrency(): string {
    try {
      return (
        this.invoice.currency ??
        this.clinicService.clinic.settings.defaultCurrency ??
        'USD'
      )
    } catch (ex) {
      return 'USD'
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.invoice) {
      this.calculatePendingAmount()
    }
  }

  ngOnDestroy() {
    this.onDestroy$.next()
    this.onDestroy$.complete()
  }

  download(event: MouseEvent) {
    const asHtml = event.altKey
    this.progressBar.mode = 'indeterminate'
    this.restService
      .generateInvoicePDF(this.invoice.id, asHtml)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (resp) => {
          this.fileService.saveAs(resp.data)
          this.progressBar.mode = 'determinate'
        },
        () => {
          this.progressBar.mode = 'determinate'
        },
      )
  }

  void() {
    // Allow only if there is no payments
    if (this.invoice.amountPaid === 0) {
      // ask for void reason
      this.confirmService
        .confirm(`Are you sure you want to void this invoice?`)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe((confirmation) => {
          if (confirmation) {
            this.delete()
          }
        })
    } else {
      const progress = this.progressService.start()
      progress.stop(
        'Please refund the remaining transactions before void an invoice',
      )
    }
  }

  delete() {
    const progress = this.progressService.start()

    this.restService
      .delete<InvoiceData>(`/invoices/${this.invoice.id}`)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((resp) => {
        progress.stop('Voided Invoice')
        this.invoice.status = Invoice.STATUS.VOID
        this.voidInvoice.emit(new InvoiceModel(resp.data))
      })
  }

  private calculatePendingAmount() {
    this.pendingAmount = 0
    this.restService
      .getPatientTransactions(this.patient.id, { invoice: this.invoice.id })
      .subscribe((transactions) => {
        if (transactions.length > 0) {
          transactions.forEach((transaction) => {
            if (transaction.status === Transaction.STATUS.PENDING) {
              this.pendingAmount += transaction.amount
            }
          })
        }
      })
  }

  openSendDialog(what: 'link' | 'pdf', method?: 'email' | 'sms') {
    this.sendContactEditDialogService.open({
      method: method ?? 'email',
      phone: this.patient?.phone,
      email: this.patient?.email,
    })
      .pipe(
        takeUntil(this.onDestroy$),
        filter(Boolean),
      )
      .subscribe((result: SendContactEditDialogResult) => {
        this.sendInvoice(what, method, result.to)
      })
  }

  sendInvoice(what: 'link' | 'pdf', method?: 'email' | 'sms', to?: string) {
    const progress = this.progressService.start()
    const params = { what, method, to }
    this.progressBar.mode = 'indeterminate'
    this.restService
      .sendInvoiceMessage(this.invoice.id, params)
      .pipe(
        takeUntil(this.onDestroy$),
        finalize(() => {
          this.progressBar.mode = 'determinate'
        }),
      )
      .subscribe({
        next: () => {
          progress.stop(`Invoice ${what} has been sent`)
        },
        error: err => {
          const message = get(err, 'error.message', 'Please try again.')
          progress.stop(`Failed to send invoice ${what}. ${message}`)
        },
      })
  }

  isEmailSendDisabled(): boolean {
    return !this.patient || !this.patient.canEmail || this.isInvoicePaid()
  }

  isSmsSendDisabled(): boolean {
    return !this.patient || !this.patient.canText || this.isInvoicePaid()
  }

  isReceiptSendDisabled(): boolean {
    return !this.patient || !this.patient.canEmail
  }

  private isInvoicePaid(): boolean {
    return this.invoice?.status === Invoice.STATUS.PAID
  }
}
